import { useState, useEffect, useRef, useContext } from 'react';
import axios from 'axios';
import { API, AuthContext } from '../App';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { toast } from 'sonner';
import { MessageCircle, X, Send, Loader2, User, Shield, Circle } from 'lucide-react';

export default function LiveChat() {
  const { user } = useContext(AuthContext);
  const [isOpen, setIsOpen] = useState(false);
  const [chat, setChat] = useState(null);
  const [messages, setMessages] = useState([]);
  const [inputMessage, setInputMessage] = useState('');
  const [loading, setLoading] = useState(false);
  const [sending, setSending] = useState(false);
  const [adminOnline, setAdminOnline] = useState(false);
  const [wsConnected, setWsConnected] = useState(false);
  
  const messagesEndRef = useRef(null);
  const wsRef = useRef(null);

  // Check admin online status
  useEffect(() => {
    const checkAdminStatus = async () => {
      // try {
      //   const response = await axios.get(`${API}/support/admin/online-status`);
      //   setAdminOnline(response.data.admins_online);
      // } catch (error) {
      //   console.error('Failed to check admin status');
      // }
    };
    
    checkAdminStatus();
    const interval = setInterval(checkAdminStatus, 30000); // Check every 30s
    
    return () => clearInterval(interval);
  }, []);

  // Check for existing chat on open
  useEffect(() => {
    if (isOpen && user) {
      checkExistingChat();
    }
  }, [isOpen, user]);

  // WebSocket connection
  useEffect(() => {
    if (chat?.id && isOpen) {
      connectWebSocket(chat.id);
    }
    
    return () => {
      if (wsRef.current) {
        wsRef.current.close();
      }
    };
  }, [chat?.id, isOpen]);

  // Auto-scroll to bottom
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  const connectWebSocket = (chatId) => {
    try {
      // Construct WebSocket URL - handle both http and https
      let wsUrl = API.replace('/api', '');
      if (wsUrl.startsWith('https://')) {
        wsUrl = wsUrl.replace('https://', 'wss://');
      } else if (wsUrl.startsWith('http://')) {
        wsUrl = wsUrl.replace('http://', 'ws://');
      }
      
      const ws = new WebSocket(`${wsUrl}/ws/support/user/${chatId}`);
      
      ws.onopen = () => {
        console.log('User support WebSocket connected');
        setWsConnected(true);
      };
      
      ws.onmessage = (event) => {
        const data = JSON.parse(event.data);
        
        if (data.type === 'message') {
          setMessages(prev => [...prev, data.message]);
        } else if (data.type === 'admin_joined') {
          setChat(prev => ({ ...prev, status: 'active', admin_name: data.admin_name }));
          toast.success(`${data.admin_name} has joined the chat`);
        } else if (data.type === 'chat_closed') {
          setChat(prev => ({ ...prev, status: 'closed' }));
          toast.info('Chat has been closed');
        }
      };
      
      ws.onerror = (error) => {
        console.error('WebSocket error:', error);
        setWsConnected(false);
      };
      
      ws.onclose = () => {
        console.log('User support WebSocket closed');
        setWsConnected(false);
      };
      
      wsRef.current = ws;
    } catch (error) {
      console.error('Failed to connect WebSocket:', error);
      setWsConnected(false);
    }
  };

  // Polling fallback for messages when WebSocket is not connected
  useEffect(() => {
    if (!wsConnected && chat?.id && isOpen) {
      const pollMessages = async () => {
        const token = localStorage.getItem('token');
        try {
          const response = await axios.get(`${API}/support/chat/${chat.id}/messages`, {
            headers: { Authorization: `Bearer ${token}` }
          });
          if (response.data.messages) {
            setMessages(response.data.messages);
          }
          if (response.data.status && response.data.status !== chat.status) {
            setChat(prev => ({ ...prev, status: response.data.status }));
          }
        } catch (error) {
          console.error('Failed to poll messages');
        }
      };
      
      // Poll every 3 seconds when WS is not connected
      const interval = setInterval(pollMessages, 3000);
      return () => clearInterval(interval);
    }
  }, [wsConnected, chat?.id, isOpen]);

  const checkExistingChat = async () => {
    setLoading(true);
    const token = localStorage.getItem('token');
    
    try {
      const response = await axios.get(`${API}/support/my-chat`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (response.data.chat) {
        setChat(response.data.chat);
        setMessages(response.data.chat.messages || []);
      }
    } catch (error) {
      console.error('Failed to check existing chat');
    } finally {
      setLoading(false);
    }
  };

  const startChat = async () => {
    setLoading(true);
    const token = localStorage.getItem('token');
    
    try {
      const response = await axios.post(`${API}/support/start-chat`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      setChat({
        id: response.data.chat_id,
        status: response.data.status
      });
      
      toast.success('Chat started! Waiting for admin...');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to start chat');
    } finally {
      setLoading(false);
    }
  };

  const sendMessage = async (e) => {
    e.preventDefault();
    if (!inputMessage.trim() || !chat?.id) return;
    
    setSending(true);
    
    // Send via WebSocket if connected
    if (wsRef.current?.readyState === WebSocket.OPEN) {
      wsRef.current.send(JSON.stringify({
        type: 'message',
        content: inputMessage
      }));
      setInputMessage('');
      setSending(false);
    } else {
      // Fallback to HTTP
      const token = localStorage.getItem('token');
      try {
        await axios.post(`${API}/support/chat/${chat.id}/send`, 
          { content: inputMessage },
          { headers: { Authorization: `Bearer ${token}` } }
        );
        setInputMessage('');
        // Refresh messages
        const msgResponse = await axios.get(`${API}/support/chat/${chat.id}/messages`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setMessages(msgResponse.data.messages);
      } catch (error) {
        toast.error('Failed to send message');
      } finally {
        setSending(false);
      }
    }
  };

  const closeChat = async () => {
    if (!chat?.id) return;
    
    const token = localStorage.getItem('token');
    try {
      await axios.post(`${API}/support/chat/${chat.id}/close`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setChat(null);
      setMessages([]);
      toast.success('Chat closed');
    } catch (error) {
      toast.error('Failed to close chat');
    }
  };

  if (!user) return null;

  return (
    <>
      {/* Chat Button */}
      <button
        onClick={() => setIsOpen(!isOpen)}
        className=" hidden fixed bottom-20 md:bottom-6 right-4 md:right-6 z-40 p-3 md:p-4 rounded-full bg-gradient-to-r from-crown-gold to-yellow-500 text-black shadow-lg hover:shadow-xl transition-all hover:scale-105"
      >
        {isOpen ? (
          <X className="w-6 h-6" />
        ) : (
          <div className="relative">
            <MessageCircle className="w-6 h-6" />
            {adminOnline && (
              <span className="absolute -top-1 -right-1 w-3 h-3 bg-green-500 rounded-full border-2 border-yellow-500"></span>
            )}
          </div>
        )}
      </button>

      {/* Chat Window */}
      {isOpen && (
        <div className="fixed bottom-36 md:bottom-24 right-4 md:right-6 z-40 w-96 max-w-[calc(100vw-2rem)] bg-gray-900 border border-gray-700 rounded-xl shadow-2xl overflow-hidden">
          {/* Header */}
          <div className="bg-gradient-to-r from-gray-800 to-gray-900 p-4 border-b border-gray-700">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="p-2 rounded-full bg-crown-gold/20">
                  <Shield className="w-5 h-5 text-crown-gold" />
                </div>
                <div>
                  <h3 className="font-bold text-white">Live Support</h3>
                  <div className="flex items-center gap-2 text-xs">
                    <Circle className={`w-2 h-2 ${adminOnline ? 'text-green-400 fill-green-400' : 'text-gray-500 fill-gray-500'}`} />
                    <span className={adminOnline ? 'text-green-400' : 'text-gray-500'}>
                      {adminOnline ? 'Admin Online' : 'Admin Offline'}
                    </span>
                  </div>
                </div>
              </div>
              {chat && chat.status !== 'closed' && (
                <button onClick={closeChat} className="text-gray-400 hover:text-white text-xs">
                  End Chat
                </button>
              )}
            </div>
          </div>

          {/* Chat Content */}
          <div className="h-80 overflow-y-auto p-4 space-y-3 bg-gray-900/50">
            {loading ? (
              <div className="flex items-center justify-center h-full">
                <Loader2 className="w-6 h-6 text-crown-gold animate-spin" />
              </div>
            ) : !chat ? (
              <div className="flex flex-col items-center justify-center h-full text-center">
                <MessageCircle className="w-12 h-12 text-gray-600 mb-4" />
                <p className="text-white font-semibold mb-2">Need Help?</p>
                <p className="text-soft-gray text-sm mb-4">
                  {adminOnline 
                    ? 'An admin is available to assist you.'
                    : 'Leave a message and we\'ll respond soon.'}
                </p>
                <Button onClick={startChat} className="btn-primary">
                  Start Chat
                </Button>
              </div>
            ) : chat.status === 'waiting' ? (
              <div className="flex flex-col items-center justify-center h-full text-center">
                <Loader2 className="w-8 h-8 text-crown-gold animate-spin mb-4" />
                <p className="text-white font-semibold">Waiting for Admin</p>
                <p className="text-soft-gray text-sm mt-2">
                  An admin will join shortly...
                </p>
              </div>
            ) : (
              <>
                {messages.length === 0 ? (
                  <div className="text-center text-soft-gray text-sm py-8">
                    {chat.admin_name ? `${chat.admin_name} has joined. Start chatting!` : 'No messages yet'}
                  </div>
                ) : (
                  messages.map((msg) => (
                    <div
                      key={msg.id}
                      className={`flex ${msg.sender_type === 'user' ? 'justify-end' : 'justify-start'}`}
                    >
                      <div
                        className={`max-w-[80%] rounded-lg px-3 py-2 ${
                          msg.sender_type === 'user'
                            ? 'bg-crown-gold text-black'
                            : 'bg-gray-700 text-white'
                        }`}
                      >
                        <p className="text-xs font-semibold mb-1 opacity-70">
                          {msg.sender_type === 'admin' ? (
                            <span className="flex items-center gap-1">
                              <Shield className="w-3 h-3" />
                              {msg.sender_name}
                            </span>
                          ) : (
                            'You'
                          )}
                        </p>
                        <p className="text-sm">{msg.content}</p>
                        <p className="text-xs opacity-50 mt-1">
                          {new Date(msg.timestamp).toLocaleTimeString()}
                        </p>
                      </div>
                    </div>
                  ))
                )}
                <div ref={messagesEndRef} />
              </>
            )}
          </div>

          {/* Input */}
          {chat && chat.status !== 'closed' && chat.status !== 'waiting' && (
            <form onSubmit={sendMessage} className="p-3 border-t border-gray-700 bg-gray-800">
              <div className="flex gap-2">
                <Input
                  value={inputMessage}
                  onChange={(e) => setInputMessage(e.target.value)}
                  placeholder="Type a message..."
                  className="flex-1 bg-gray-900 border-gray-700 text-white"
                  disabled={sending}
                />
                <Button type="submit" disabled={sending || !inputMessage.trim()} className="btn-primary px-3">
                  {sending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
                </Button>
              </div>
              {!wsConnected && (
                <p className="text-xs text-yellow-400 mt-1">Reconnecting...</p>
              )}
            </form>
          )}

          {chat?.status === 'closed' && (
            <div className="p-4 border-t border-gray-700 bg-gray-800 text-center">
              <p className="text-soft-gray text-sm mb-2">This chat has ended</p>
              <Button onClick={() => { setChat(null); setMessages([]); }} variant="outline" size="sm">
                Start New Chat
              </Button>
            </div>
          )}
        </div>
      )}
    </>
  );
}
